import {
  differenceInDays,
  eachDayOfInterval,
  eachHourOfInterval,
  startOfDay,
  startOfHour,
} from 'date-fns';
import { AccountDateFilterRequest } from '../../../_shared/models/account-date-filter-request.model';
import {
  BudgetVsActualInsightsResponse,
  CostDistributionSummary,
  CostSummary,
  MaintenanceInsightsResponse,
  MaintenanceKpiResponse,
} from '../models/cost-tracking-response.model';
import {
  ACCOUNT_SLOT_COUNT,
  ANNUAL_BUDGET,
  CATEGORY_PROFILES,
  COST_CATEGORIES,
  COST_LEDGER,
  CostCategory,
  CostLedgerEntry,
  DAYS_PER_WEEK,
  DAYS_PER_YEAR,
  HOURS_PER_DAY,
  MAINTENANCE_BENCHMARK_PER_DAY,
  MAINTENANCE_CATEGORIES,
  MONTHS_PER_YEAR,
} from './cost-tracking.mock';

interface Period {
  timeFrom: Date;
  timeTo: Date;
}

interface TimeSeriesPoint {
  timeSeries: Date;
  totalValue: number;
}

interface SelectedEntries {
  period: Period;
  all: CostLedgerEntry[];
  current: CostLedgerEntry[];
  previous: CostLedgerEntry[];
}

const PERCENT = 100;
const CHART_HOURS_PER_BUCKET = 6;
const CHART_DAILY_MAX_DAYS = 7;

export function buildCategoryCostSummary(
  request: AccountDateFilterRequest,
  category: CostCategory
): CostSummary {
  const { current, previous } = selectEntries(request);
  return toCostSummary(
    sumAmounts(current, [category]),
    sumAmounts(previous, [category])
  );
}

export function buildAnnualBudgetSummary(
  request: AccountDateFilterRequest
): CostSummary {
  const spend = sumAmounts(selectEntries(request).current);
  return {
    totalValue: ANNUAL_BUDGET,
    previousValue: spend,
    percentageChange: toPercentage(spend, ANNUAL_BUDGET),
    costDifference: ANNUAL_BUDGET - spend,
  };
}

export function buildCostDistributionSummary(
  request: AccountDateFilterRequest
): CostDistributionSummary {
  const { current } = selectEntries(request);
  const reactive = current.filter((entry) => entry.kind === 'reactive');
  const preventive = current.filter((entry) => entry.kind === 'preventive');
  const reactiveCost = sumAmounts(reactive);
  const preventiveCost = sumAmounts(preventive);
  const total = reactiveCost + preventiveCost;
  const reactivePercentage =
    total > 0 ? Math.round((reactiveCost / total) * PERCENT) : 0;

  return {
    reactiveCost,
    reactivePercentage,
    unplannedRepairsCount: reactive.length,
    preventiveCost,
    preventivePercentage: total > 0 ? PERCENT - reactivePercentage : 0,
    servicesCompletedCount: preventive.length,
  };
}

export function buildMaintenanceInsights(
  request: AccountDateFilterRequest
): MaintenanceInsightsResponse[] {
  return buildActualSeries(request, MAINTENANCE_CATEGORIES);
}

export function buildMaintenanceBenchmark(
  request: AccountDateFilterRequest
): number {
  const days = differenceInDays(request.timeTo, request.timeFrom);
  if (days === 0) {
    return Math.round(
      (MAINTENANCE_BENCHMARK_PER_DAY / HOURS_PER_DAY) * CHART_HOURS_PER_BUCKET
    );
  }
  if (days <= CHART_DAILY_MAX_DAYS) return MAINTENANCE_BENCHMARK_PER_DAY;
  return MAINTENANCE_BENCHMARK_PER_DAY * DAYS_PER_WEEK;
}

export function buildMaintenanceSummary(
  request: AccountDateFilterRequest
): MaintenanceKpiResponse {
  return buildKpiSummary(request, MAINTENANCE_CATEGORIES);
}

export function buildBudgetedInsights(
  request: AccountDateFilterRequest
): BudgetVsActualInsightsResponse[] {
  const { period } = selectEntries(request);
  const hourly = isHourlyRange(request);
  const totalValue = COST_CATEGORIES.reduce(
    (sum, category) =>
      sum + bucketBudget(categoryAnnualBudget(category), hourly),
    0
  );

  return listBuckets(period, hourly).map((bucket) => ({
    timeSeries: clampToPeriod(bucket, period),
    totalValue,
  }));
}

export function buildActualInsights(
  request: AccountDateFilterRequest
): BudgetVsActualInsightsResponse[] {
  return buildActualSeries(request, COST_CATEGORIES);
}

export function buildBudgetVsActualSummary(
  request: AccountDateFilterRequest
): MaintenanceKpiResponse {
  return buildKpiSummary(request, COST_CATEGORIES);
}

function selectEntries(request: AccountDateFilterRequest): SelectedEntries {
  const slots = toAccountSlots(request.accountIds);
  const period = clampToNow(request);
  const previous = previousPeriod(period);
  const all = COST_LEDGER.filter(
    (entry) => slots === null || slots.has(entry.accountSlot)
  );

  return {
    period,
    all,
    current: all.filter((entry) => isInPeriod(entry, period)),
    previous: all.filter((entry) => isInPeriod(entry, previous)),
  };
}

function clampToNow(period: Period): Period {
  const now = new Date();
  return {
    timeFrom: period.timeFrom,
    timeTo: period.timeTo < now ? period.timeTo : now,
  };
}

function toAccountSlots(accountIds: number[]): Set<number> | null {
  if (!accountIds.length) return null;
  return new Set(accountIds.map((id) => Math.abs(id) % ACCOUNT_SLOT_COUNT));
}

function isInPeriod(entry: CostLedgerEntry, period: Period): boolean {
  return entry.date >= period.timeFrom && entry.date <= period.timeTo;
}

function previousPeriod(period: Period): Period {
  const length = period.timeTo.getTime() - period.timeFrom.getTime();
  const timeTo = new Date(period.timeFrom.getTime() - 1);
  return { timeFrom: new Date(timeTo.getTime() - length), timeTo };
}

function sumAmounts(
  entries: CostLedgerEntry[],
  categories?: CostCategory[]
): number {
  return entries.reduce(
    (sum, entry) =>
      categories === undefined || categories.includes(entry.category)
        ? sum + entry.amount
        : sum,
    0
  );
}

function toCostSummary(current: number, previous: number): CostSummary {
  return {
    totalValue: current,
    previousValue: previous,
    percentageChange: toPercentageChange(current, previous),
    costDifference: current - previous,
  };
}

function buildKpiSummary(
  request: AccountDateFilterRequest,
  categories: CostCategory[]
): MaintenanceKpiResponse {
  const { current, previous } = selectEntries(request);
  const currentValue = sumAmounts(current, categories);
  const previousValue = sumAmounts(previous, categories);

  return {
    currentValue,
    previousValue,
    percentageChange: toPercentageChange(currentValue, previousValue),
  };
}

function buildActualSeries(
  request: AccountDateFilterRequest,
  categories: CostCategory[]
): TimeSeriesPoint[] {
  const { period, current } = selectEntries(request);
  const hourly = isHourlyRange(request);
  const toBucket = hourly ? startOfHour : startOfDay;
  const totals = new Map<number, number>();

  for (const entry of current) {
    if (!categories.includes(entry.category)) continue;
    const key = toBucket(entry.date).getTime();
    totals.set(key, (totals.get(key) ?? 0) + entry.amount);
  }

  return listBuckets(period, hourly).map((bucket) => ({
    timeSeries: clampToPeriod(bucket, period),
    totalValue: totals.get(bucket.getTime()) ?? 0,
  }));
}

function isHourlyRange(period: Period): boolean {
  return differenceInDays(period.timeTo, period.timeFrom) === 0;
}

function listBuckets(period: Period, hourly: boolean): Date[] {
  if (period.timeTo < period.timeFrom) return [];
  const interval = { start: period.timeFrom, end: period.timeTo };
  return hourly ? eachHourOfInterval(interval) : eachDayOfInterval(interval);
}

function clampToPeriod(bucket: Date, period: Period): Date {
  return bucket < period.timeFrom ? period.timeFrom : bucket;
}

function categoryAnnualBudget(category: CostCategory): number {
  return CATEGORY_PROFILES[category].monthlyBudget * MONTHS_PER_YEAR;
}

function bucketBudget(annualAmount: number, hourly: boolean): number {
  const daily = annualAmount / DAYS_PER_YEAR;
  return Math.round(hourly ? daily / HOURS_PER_DAY : daily);
}

function toPercentage(part: number, whole: number): number {
  return whole > 0 ? roundToHundredths((part / whole) * PERCENT) : 0;
}

function toPercentageChange(current: number, previous: number): number {
  if (previous > 0) return toPercentage(current - previous, previous);
  return current > 0 ? PERCENT : 0;
}

function roundToHundredths(value: number): number {
  return Math.round(value * PERCENT) / PERCENT;
}